import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

import type { JourneyState, SheetSnap } from '../app/journey-state';

export interface TravelSheetProps {
  state: JourneyState;
  title: string;
  labels: Record<SheetSnap, string> & { close: string };
  children: ReactNode;
  restoreFocusRef?: RefObject<HTMLElement | null>;
  onEscape: () => void;
  onClose: () => void;
  onSnapChange: (snap: SheetSnap) => void;
}

export function TravelSheet({ state, title, labels, children, restoreFocusRef, onEscape, onClose, onSnapChange }: TravelSheetProps) {
  const open = state.surface === 'journey';
  const sheetRef = useRef<HTMLElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!open) return undefined;
    sheetRef.current?.focus();
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onEscapeRef.current();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      restoreFocusRef?.current?.focus();
    };
  }, [open, restoreFocusRef]);

  if (!open) return null;

  return (
    <section
      ref={sheetRef}
      className="travel-sheet"
      data-phase={state.phase}
      data-snap={state.snap}
      aria-label={title}
      tabIndex={-1}
    >
      <header className="travel-sheet__header">
        <h2>{title}</h2>
        <div className="travel-sheet__snaps" aria-label={`${title} view`}>
          {(['compact', 'medium', 'expanded'] as const).map((snap) => (
            <button
              key={snap}
              type="button"
              aria-pressed={state.snap === snap}
              onClick={() => onSnapChange(snap)}
            >
              {labels[snap]}
            </button>
          ))}
        </div>
        <button className="travel-sheet__close" type="button" onClick={onClose} aria-label={labels.close} title={labels.close}>×</button>
      </header>
      <div className="travel-sheet__content">{children}</div>
    </section>
  );
}
